import { useState, useEffect, useRef } from 'react';
import EnrolledCourseService from '../../../services/coursesApi/EnrolledCourseService';
import { useAuth } from '../../../context/AuthContext';

/**
 * Simple hook to fetch enrolled courses with minimal options
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Limit number of courses returned (0 for all)
 * @param {boolean} options.showDescription - Whether to include batch name as description
 * @returns {Object} Courses data and loading state
 */

const useEnrolledCourses = ({ limit = 0, showDescription = true } = {}) => {
  const [formattedCourses, setFormattedCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { refreshTokenSilently } = useAuth();
  const abortControllerRef = useRef(null);
  
  // Fetch courses on component mount
  useEffect(() => {
    const fetchCourses = async () => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      setIsLoading(true);
      try {
        // Get courses from API
        const response = await EnrolledCourseService.getEnrolledCourses(
          abortControllerRef.current.signal
        );
        
        // Get raw courses data
        let coursesData = response.data || [];
        
        // Apply limit if specified
        if (limit > 0) {
          coursesData = coursesData.slice(0, limit);
        }
        
        // Format courses for components
        const formatted = coursesData.map(course => {
          const courseDetails = course.courseId || {};
          const batch = courseDetails.batches?.find(b => b._id === course.batchId) || {};
          
          return {
            id: course._id,
            courseId: courseDetails._id,
            batchId: course.batchId,
            image: {
              src: courseDetails.thumbnail || "https://via.placeholder.com/100x100?text=Course",
              alt: courseDetails.courseName
            },
            label: batch.batchType || "",
            showLabel: !!batch.batchType,
            title: courseDetails.courseName || "Untitled Course",
            description: batch.name || "",
            showDescription: showDescription
          };
        });
        
        setFormattedCourses(formatted);
        setError(null);
      } catch (err) {
        console.error("Error fetching enrolled courses:", err);
        if (err.name === 'AbortError') {
          // Request was aborted, don't update state
          return;
        }
        
        if (err.response?.status === 401) {
          // Try to refresh token on auth error
          try {
            const refreshed = await refreshTokenSilently();
            if (refreshed) {
              // If successful, retry the fetch
              refetch();
              return;
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
          }
        }
        
        setError("Failed to load your courses");
      } finally {
        if (!abortControllerRef.current.signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchCourses();
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [limit, showDescription]);

  // Function to retry fetching
  const refetch = () => {
    setIsLoading(true);
    setError(null);
    // The useEffect will run again automatically
  };

  return { formattedCourses, isLoading, error, refetch };
};

export default useEnrolledCourses;